import { shortShareUrl } from '@/common/shareLink';
import { scrollPageToTop } from '@/common/scrollPageToTop';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import Container from '@/components/common/elements/Container';
import { CONTENT_MAX_WIDTH, CONTENT_PADDING_X } from '@/styles/layout';
import Text from '@/components/common/elements/Text';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { userAtom } from '@/state/loginState';
import { unreadNotificationCountAtom } from '@/state/notificationState';
import { useUnreadNotificationsPolling } from '@/common/useUnreadNotifications';
import { useInputKeyboardSignal } from '@/common/inputKeyboard';
import { toastState } from '@/state/toastState';
import { useAuth, useIsMobile } from '@/common/hooks';
import { useMediaQuery } from '@mui/material';
import { api } from '@/api/repository/axios';
import { designSystem } from '@/styles/theme';
import { profileCache } from '@/utils/profileCache';
import CustomModal from '@/components/common/elements/Modal';
import { default as ModalButton } from '@/components/common/elements/Button';
import {
  Menu as MenuIcon,
  Person as PersonIcon,
  Notifications as NotificationsIcon,
  NotificationsNoneOutlined as BellIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  ArrowDropDown as ArrowDropDownIcon,
  LightbulbOutlined as LightbulbOutlinedIcon,
  ArrowBackIosNew as ArrowBackIosNewIcon,
  IosShare as IosShareIcon,
} from '@mui/icons-material';
import Logo from '@/assets/icons/Logo';
import HeaderSearchIcon from '@/assets/icons/HeaderSearchIcon';
import TopAnnouncementBar from '@/components/layout/TopAnnouncementBar';
import BottomHomeIcon from '@/assets/icons/BottomHomeIcon';
import BottomHiringIcon from '@/assets/icons/BottomHiringIcon';
import BottomSeminarIcon from '@/assets/icons/BottomSeminarIcon';
import BottomCommunityIcon from '@/assets/icons/BottomCommunityIcon';
import BottomMyPageIcon from '@/assets/icons/BottomMyPageIcon';
import MobileDrawerMenu from '@/components/layout/MobileDrawerMenu';
import { handlePageHeaderBack } from '@/common/headerBack';
import { lockBodyScroll } from '@/common/bodyScrollLock';
import { useBackToClose } from '@/common/useBackToClose';
import { useAiBackgroundLocation } from '@/pages/AiChat/aiSheetRoute';
import { claudeTokens } from '@/styles/claudeTokens';
import { track } from '@/common/analytics';
import { UI_ACTION, detailTargetOfPath } from '@/common/uiEventNames';

const HeaderWrapper = styled.header`
  width: 100%;
  background-color: #fff;
  box-shadow: none;
  position: sticky;
  top: 0;
  z-index: 1000;
  left: 0;
  right: 0;
  /* iOS 앱(노치/다이나믹 아일랜드) 상태바 겹침 방지 */
  padding-top: env(safe-area-inset-top, 0px);

  /* iOS inset은 실제 상태바보다 여유가 커서 앱에서는 살짝 줄여 밀착 */
  html.platform-ios & {
    padding-top: max(calc(env(safe-area-inset-top, 0px) - 10px), 0px);
  }

  /* 글쓰기 화면(components/common/write)은 폰에서 앱 헤더 대신 자기 위 줄([✕] 글쓰기 [등록])을 쓴다 */
  @media (max-width: 768px) {
    body[data-write-screen='true'] & {
      display: none;
    }
  }
`;

const MainHeader = styled.header`
  background-color: white;
  border-bottom: 1px solid #e5e7eb;
`;

const HeaderContainer = styled.div`
  max-width: ${CONTENT_MAX_WIDTH};
  margin: 0 auto;
  padding: 0 ${CONTENT_PADDING_X};

  @media (max-width: 768px) {
    padding: 0 0.5rem;
  }
`;

const TopSection = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
  padding-top: 0.5rem;

  /* 상세 헤더(DetailNavBar 48px)와 높이를 통일해 화면 전환 시 헤더 깜박임 방지 */
  @media (max-width: 768px) {
    margin-bottom: 0;
    padding-top: 0;
    padding-bottom: 0;
    height: 48px;
  }
`;

const LogoWrapper = styled.div`
  cursor: pointer;
  flex-shrink: 0;
  display: flex;
  align-items: center;

  @media (max-width: 768px) {
    svg {
      width: 160px;
      height: 40px;
    }
  }
`;

const BrandLogo = styled.span`
  font-family: 'Pretendard Variable', Pretendard, system-ui, sans-serif;
  font-size: 1.75rem;
  font-weight: 800;
  color: ${claudeTokens.color.text1};
  letter-spacing: -0.03em;
  line-height: 1;
  user-select: none;

  &::after {
    content: '';
    display: inline-block;
    width: 0.32em;
    height: 0.32em;
    background: #5A9B96;
    border-radius: 50%;
    margin-left: 0.08em;
    vertical-align: baseline;
  }

  @media (max-width: 768px) {
    font-size: 1.45rem;
  }
`;

const SearchWrapper = styled.div`
  max-width: 18.5rem; /* PC: 원래 크기 */
  margin-left: auto; /* 오른쪽 정렬 */
  position: relative;

  @media (max-width: 768px) {
    display: none; /* 모바일에서 검색창 숨김 */
  }
`;

const MobileSearchButton = styled.button`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
    background: none;
    border: none;
    color: ${designSystem.colors.primary};
    cursor: pointer;
    padding: 6px;
    margin-left: auto;
    border-radius: 50%;

    &:active {
      background: ${designSystem.colors.background.light};
    }
  }
`;


const SearchInput = styled.input`
  width: 100%;
  border: 1px solid #5A9B96;
  border-radius: 9999px;
  padding: 0.7rem 3rem 0.7rem 1.4rem;
  font-size: 0.9rem;
  line-height: 1.4rem;
  outline: none;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;

  &::placeholder {
    text-overflow: ellipsis;
  }

  &:focus {
    border-color: #3D7E79;
  }

  @media (max-width: 768px) {
    font-size: 0.813rem;
    padding: 0.438rem 1.9rem 0.438rem 0.625rem;
  }
`;

const SearchIconWrapper = styled.div`
  position: absolute;
  right: 1.5rem;
  top: 50%;
  transform: translateY(-50%);
  color: #5A9B96;
  cursor: pointer;

  @media (max-width: 768px) {
    right: 0.375rem;

    svg {
      font-size: 18px !important;
    }
  }
`;

const ClearButton = styled.button`
  position: absolute;
  right: 3.5rem;
  top: 50%;
  transform: translateY(-50%);
  background: #e5e7eb;
  border: none;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  color: #6b7280;
  transition: all 0.2s;

  &:hover {
    background: #d1d5db;
    color: #374151;
  }

  svg {
    font-size: 14px !important;
  }

  @media (max-width: 768px) {
    right: 2rem;
    width: 18px;
    height: 18px;

    svg {
      font-size: 12px !important;
    }
  }
`;

const Nav = styled.nav`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 0;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const MenuList = styled.ul`
  display: flex;
  list-style: none;
  padding: 0;
  margin: 0;
  gap: 3.5rem; /* Increased Gap */

  /* 좁은 PC 폭(태블릿·줄인 창): 메뉴와 사용자 메뉴가 한 줄에 들어가게 간격을 줄인다 — 글자가 두 줄로 꺾이지 않게 */
  @media (max-width: 1024px) {
    gap: clamp(16px, 2.2vw, 28px);
  }
`;

const MenuItem = styled.li`
  font-size: 1.125rem;
  line-height: 1.75rem;
  font-weight: 600; /* Updated Font Weight */
  color: #1F2937; /* Updated Text Color */
  cursor: pointer;
  transition: color 0.2s;
  white-space: nowrap;

  @media (max-width: 900px) {
    font-size: 1rem;
  }
  
  &:hover {
    color: ${designSystem.colors.primary};
  }
`;

const UserMenuList = styled(MenuList)`
  gap: 1.25rem;
  min-width: 0;

  @media (max-width: 1024px) {
    gap: 0.75rem;
  }
`;

const UserMenuItem = styled.li`
  font-size: 0.875rem;
  line-height: 1.25rem;
  color: ${designSystem.colors.text.light};
  cursor: pointer;
  transition: color 0.2s;
  white-space: nowrap;
  
  &:hover {
    color: ${designSystem.colors.text.primary};
  }
`;

/** 이름 — 길면 말줄임(메뉴 줄이 넘치지 않게) */
const UserNameItem = styled(UserMenuItem)`
  max-width: 10em;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/** 치과 회원의 [채용 등록] — 이름 자리에 둔다. 옆 메뉴와 같은 부품, 주된 행동이라 글자만 브랜드 청록·600(버튼 모양 없음) */
const UserPrimaryItem = styled(UserMenuItem)`
  color: ${designSystem.colors.primary};
  font-weight: 600;

  &:hover {
    color: ${designSystem.colors.primaryDark};
  }
`;

/**
 * PC 헤더 벨 항목. 모바일 BellButton 과 동일한 기하(아이콘 주변 6px 패딩)를 줘서
 * BellBadge(top:2px, right:0)가 아이콘 위를 덮지 않고 우상단 바깥 모서리에 걸리게 한다.
 */
const UserBellItem = styled(UserMenuItem)`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  margin: -6px 0;
  line-height: 0;
  border-radius: 50%;
  color: ${designSystem.colors.primary};

  &:hover {
    color: ${designSystem.colors.primaryDark};
  }
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 0;
`;

const UserSection = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  
  @media (max-width: 768px) {
    gap: 4px;
  }
`;

const LoginButton = styled.button`
  background: ${designSystem.colors.primary};
  color: white;
  border: none;
  border-radius: 20px;
  padding: 8px 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: ${designSystem.colors.primaryDark};
    transform: translateY(-1px);
  }
  
  @media (max-width: 768px) {
    padding: 6px 12px;
    font-size: 14px;
  }
`;

const SignUpButton = styled.button`
  background: transparent;
  color: ${designSystem.colors.text.primary};
  border: 1px solid ${designSystem.colors.border.medium};
  border-radius: 20px;
  padding: 8px 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    border-color: ${designSystem.colors.primary};
    color: ${designSystem.colors.primary};
  }
  
  @media (max-width: 768px) {
    padding: 6px 12px;
    font-size: 14px;
  }
`;

const UserProfile = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 8px 12px;
  border-radius: 20px;
  transition: background 0.2s;
  
  &:hover {
    background: ${designSystem.colors.background.light};
  }
`;

const UserName = styled.span`
  font-weight: 500;
  color: ${designSystem.colors.text.primary};
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const NotificationButton = styled.button`
  background: none;
  border: none;
  color: ${designSystem.colors.text.light};
  cursor: pointer;
  position: relative;
  padding: 8px;
  border-radius: 50%;
  transition: all 0.2s;
  
  &:hover {
    background: ${designSystem.colors.background.light};
    color: ${designSystem.colors.primary};
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: ${designSystem.colors.text.primary};
  cursor: pointer;
  padding: 8px;

  @media (max-width: 768px) {
    display: none; // Hidden - using bottom nav instead
  }
`;

/** 알림 종 버튼 — 모바일 상단·상세 내비·데스크탑 공통. 안 읽은 수 배지 포함 */
const BellButton = styled.button`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  color: ${designSystem.colors.primary};
  cursor: pointer;
  padding: 6px;
  border-radius: 50%;

  &:active {
    background: ${designSystem.colors.background.light};
  }
`;

/*
 * 알림 수 배지 — 왼쪽 끝을 종 가운데 조금 오른쪽에 고정해 글자가 길어지면 바깥(오른쪽)으로 자란다.
 * 오른쪽 끝을 고정하던 때는 "99+" 가 종 아이콘 폭 대부분을 가렸다(2026-10-11). 흰 띠로 종과 경계를 나눈다.
 */
const BellBadge = styled.span`
  position: absolute;
  top: 1px;
  left: calc(50% + 2px);
  pointer-events: none;
  box-shadow: 0 0 0 2px #fff;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 8px;
  background: #e24b4a;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  line-height: 16px;
  text-align: center;
  box-sizing: border-box;
`;

const NavDot = styled.span`
  position: absolute;
  top: 6px;
  right: 14px;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #e24b4a;
`;

const MobileHamburgerButton = styled.button`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
    background: none;
    border: none;
    color: ${designSystem.colors.primary};
    cursor: pointer;
    padding: 6px;
    margin-left: 4px;
    border-radius: 50%;

    &:active {
      background: ${designSystem.colors.background.light};
    }
  }
`;

const BottomNav = styled.nav`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    width: 100%;
    background: white;
    border-top: 1px solid ${designSystem.colors.border.light};
    justify-content: space-around;
    align-items: center;
    padding: 8px 0;
    padding-bottom: calc(8px + env(safe-area-inset-bottom, 0px));
    z-index: 1000;
    box-shadow: 0 -2px 8px rgba(0,0,0,0.1);
    transform: translateZ(0);
    -webkit-transform: translateZ(0);

    /* 글쓰기 화면(components/common/write)에서는 하단 탭을 숨긴다 — 그 자리에 도구 줄이 온다 */
    body[data-write-screen='true'] & {
      display: none;
    }

    /* 입력칸에 글을 쓰는 동안(화면 키보드)·글 상세의 아래 댓글 입력 칸이 떠 있는 동안은 숨긴다 — 아이폰은 키보드가 올라오면 바닥에 고정된
       탭이 화면 중간으로 떠올라 입력칸을 가린다(2026-10-03 운영 신고) */
    body[data-input-focused='true'] &,
    body[data-comment-bar='true'] & {
      display: none;
    }
  }
`;

// 탭마다 같은 폭 — 글자 길이(속닥속닥·MY)와 상관없이 아이콘 간격이 고르다(치과 회원 5개 / 그 외 6개)
const BottomNavItem = styled.div<{ active?: boolean }>`
  position: relative;
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  cursor: pointer;
  padding: 8px 0;
  color: ${props => props.active ? designSystem.colors.primary : designSystem.colors.text.light};
  transition: color 0.2s;

  &:active {
    transform: scale(0.95);
  }
`;

const BottomNavLabel = styled.span`
  font-size: 11px;
  font-weight: 500;
`;

// 모바일 상세 페이지용 iOS 스타일 네비게이션 바 (뒤로가기 + 타이틀)
const DetailNavBar = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    position: relative;
    height: 48px;
    background: white;
    padding: 0 0.5rem;
    gap: 4px;
  }
`;

// 상세 네비게이션 우측 아이콘 그룹 (공유 + 검색 + 햄버거)
const DetailNavActions = styled.div`
  display: flex;
  align-items: center;
  margin-left: auto;
`;

// 상세 네비게이션 아이콘 버튼 — 세 버튼 모두 동일한 패딩/마진 사용
const DetailNavIconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  color: ${designSystem.colors.primary};
  cursor: pointer;
  padding: 6px;
  border-radius: 50%;

  &:active {
    background: ${designSystem.colors.background.light};
  }
`;

const DetailNavBackButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  color: ${designSystem.colors.text.primary};
  cursor: pointer;
  padding: 6px;
  border-radius: 50%;

  &:active {
    background: ${designSystem.colors.background.light};
  }
`;

// 하단 탭 최상위 화면(홈/채용/세미나/속닥속닥/전문가/마이)만 기본 헤더 노출,
// 그 외 모든 화면(상세·폼·내정보수정·이력서 등)은 iOS식 뒤로가기 네비게이션 노출
const TAB_ROUTES: Set<string> = new Set([
    '/', '/home-v1', '/home-v2', '/recruitments',
    '/recruitments/region',
    '/seminars',
    '/posts',
    '/expert-lounge',
    '/profile',
]);

// 직접 URL 진입(히스토리 없음) 시 뒤로가기의 도착지 — 섹션 목록으로, 그 외엔 홈으로
const DETAIL_NAV_FALLBACKS: [string, string][] = [
    ['/resume', '/profile'],
    ['/apply', '/profile'],
    ['/favorites', '/profile'],
    ['/inquiries', '/profile'],
    ['/inquiry', '/inquiries/my'],
    ['/payments', '/profile'],
    ['/coupons', '/profile'],
    ['/salary-surveys', '/profile'],
    ['/clinic', '/profile'],
    ['/seminar/instructor', '/profile'],
    ['/points', '/profile'],
    ['/products', '/profile'],
    ['/purchase-history', '/profile'],
    ['/posts', '/posts'],
    ['/notices', '/notices'],
    ['/seminars', '/seminars'],
    ['/expert-lounge', '/expert-lounge'],
    ['/recruitments', '/recruitments/region'],
    ['/profile', '/profile'],
];

const getDetailNavConfig = (pathname: string): { fallbackPath: string } | null => {
    const normalized: string = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
    if (TAB_ROUTES.has(normalized)) return null;
    const fallback = DETAIL_NAV_FALLBACKS.find(([prefix]) => normalized === prefix || normalized.startsWith(prefix + '/'));
    return { fallbackPath: fallback ? fallback[1] : '/' };
};

// 채용 검색창 추천 문구 — 진입 시 랜덤 1개 노출
const RECRUIT_SEARCH_PLACEHOLDERS = [
    '치과 일자리 검색',
    '내 동네 치과 알바 찾기',
    '치과위생사 채용 검색',
    '지역·지하철로 찾기',
    '주 5일 치과 구인 검색',
    '경력·신입 채용 검색',
    '오늘의 치과 채용',
    '원하는 조건으로 검색',
];

/**
 * 공유 링크의 utm_campaign — 어드민 `campaigns.campaign_key`와 반드시 일치해야 성과가 집계된다.
 * 공유 대상(글/공고 등)마다 값을 바꾸면 집계가 쪼개지므로 **고정**하고, 대상 구분은 utm_content로 한다.
 */
const SHARE_CAMPAIGN_KEY = 'user_share';

/**
 * 공유된 경로를 utm_content 태그로 변환한다(예: `/expert-lounge/6a58...` → `expert-lounge_6a58...`).
 * 어느 화면의 무엇이 공유돼 유입됐는지 구분하는 용도.
 */
const toShareContentTag = (pathname: string): string =>
    pathname.replace(/^\/+|\/+$/g, '').replace(/\//g, '_') || 'home';

const Header = () => {
    const navigate = useNavigate();
    // 입력 중(화면 키보드가 떠 있을 때) 하단 탭·폼 하단 줄 숨김 신호 + 키보드에 가린 칸 보이기(UI 가이드 7.1)
    useInputKeyboardSignal();
    // 폰 AI 시트가 열려 있으면(시트 칸) 아래 깔린 화면 기준으로 머리줄·하단 탭을 그린다
    const location = useAiBackgroundLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const [user, setUser] = useRecoilState(userAtom);
    const { isLoggedIn, logout } = useAuth();
    // 안 읽은 알림 수 — 헤더에서만 폴링, 배지·MY 탭 점에 사용
    useUnreadNotificationsPolling();
    const unreadCount = useRecoilValue(unreadNotificationCountAtom);
    const bellLabel = unreadCount > 99 ? '99+' : String(unreadCount);
    const goNotifications = () => { navigate('/notifications'); window.scrollTo(0, 0); };
    const [searchQuery, setSearchQuery] = useState('');
    // 채용 검색 추천 문구를 진입 시 한 번 랜덤 선택(렌더마다 바뀌지 않도록 고정)
    const recruitSearchPlaceholder = useMemo(
        () => RECRUIT_SEARCH_PLACEHOLDERS[Math.floor(Math.random() * RECRUIT_SEARCH_PLACEHOLDERS.length)],
        [],
    );
    const [showAlertModal, setShowAlertModal] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [alertCallback, setAlertCallback] = useState<(() => void) | null>(null);
    const [showLoginRequiredModal, setShowLoginRequiredModal] = useState(false);
    const [pendingNavigatePath, setPendingNavigatePath] = useState<string>('');
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const isMobile = useIsMobile();
    const setToast = useSetRecoilState(toastState);

    // URL에서 검색어 읽어오기
    useEffect(() => {
        const urlSearchQuery = searchParams.get('search') || '';
        setSearchQuery(urlSearchQuery);
    }, [location.pathname, searchParams]);

    const closeMobileMenu = useCallback(() => setMobileMenuOpen(false), []);
    // 메뉴를 연 채 뒤로 가기(안드로이드 뒤로 키 등)면 이전 페이지로 나가지 않고 메뉴만 닫는다(공용 시트와 같은 방문 기록 칸 방식).
    // 예전엔 메뉴는 열린 채 뒤 화면만 이전 페이지로 바뀌고, 잠금이 이전 페이지 스크롤 기준으로 남아 머리줄이 화면 위로 밀렸다
    const leaveMobileMenu = useBackToClose(mobileMenuOpen, closeMobileMenu);
    // 다른 길(스와이프 뒤로가기·링크)로 경로가 바뀌면 메뉴를 닫는다
    useEffect(() => {
        setMobileMenuOpen(false);
    }, [location.pathname]);

    // 모바일 메뉴가 열린 동안 뒤 화면이 스크롤되지 않게 body 를 고정한다(공용 잠금 — 머리줄·화면 위치는 그대로, common/bodyScrollLock).
    useEffect(() => {
        if (!mobileMenuOpen) return undefined;
        return lockBodyScroll();
    }, [mobileMenuOpen]);

    // 메뉴 항목 이동·로그아웃은 쌓은 방문 기록 칸을 먼저 되돌린 뒤 한다 — 되돌리기가 새로 쌓은 이동을 지우지 않게
    const handleMobileMenuNavigate = (path: string) => {
        closeMobileMenu();
        leaveMobileMenu(() => {
            navigate(path);
            window.scrollTo(0, 0);
        });
    };
    const handleMobileMenuLogout = () => {
        closeMobileMenu();
        leaveMobileMenu(() => void logout());
    };

    const showAlert = (message: string, callback?: () => void) => {
        setAlertMessage(message);
        setShowAlertModal(true);
        setAlertCallback(() => callback || null);
    };

    const closeAlert = () => {
        setShowAlertModal(false);
        if (alertCallback) {
            alertCallback();
            setAlertCallback(null);
        }
    };

    const navigateOrScrollToTop = (path: string) => {
        const targetPath = path.split('?')[0];
        const isHomeAlias = targetPath === '/' && location.pathname === '/recruitments';
        if (location.pathname === targetPath || isHomeAlias) {
            // 같은 목록을 다시 누르면 필터·페이지·방문 기록을 유지한다.
            scrollPageToTop();
            return;
        }
        navigate(path);
        scrollPageToTop();
    };

    const handleLoginRequiredAction = (path: string) => {
        if (isLoggedIn) {
            navigateOrScrollToTop(path);
        } else {
            setPendingNavigatePath(path);
            setShowLoginRequiredModal(true);
        }
    };

    const handleLoginConfirm = () => {
        setShowLoginRequiredModal(false);
        navigate('/login');
        window.scrollTo(0, 0);
    };

    const handleLoginRequiredModalClose = () => {
        setShowLoginRequiredModal(false);
        setPendingNavigatePath('');
    };

    useEffect(() => {
        const fetchUserMe = async () => {
            try {
                // Check cache first
                const cachedProfile = profileCache.get();
                if (cachedProfile) {
                    setUser(cachedProfile);
                    return;
                }

                // Fetch from API if no cache
                const response = await api.user.summary();
                setUser(response.result);

                // Cache the profile data
                profileCache.set(response.result);
            } catch (error: any) {
                // 401 에러는 로그인하지 않은 상태이므로 조용히 처리
                if (error?.response?.status === 401) {
                    profileCache.clear();
                    setUser(null);
                } else {
                    // 401이 아닌 다른 에러는 로그만 출력
                    console.error('Failed to fetch user profile:', error);
                    profileCache.clear();
                }
            }
        };

        if (isLoggedIn && !user) {
            fetchUserMe();
        }
    }, [isLoggedIn, user, setUser]);

    // 페이지별 검색 설정
    const getSearchConfig = () => {
        const pathname = location.pathname;
        if (pathname.startsWith('/notices')) {
            return { placeholder: '공지사항 검색', path: '/notices', param: 'search' };
        }
        if (pathname.startsWith('/seminars')) {
            return { placeholder: '세미나 검색', path: '/seminars', param: 'search' };
        }
        if (pathname.startsWith('/posts')) {
            return { placeholder: '속닥속닥 검색', path: '/posts', param: 'search' };
        }
        if (pathname.startsWith('/expert-lounge')) {
            return { placeholder: '전문가 라운지 검색', path: '/expert-lounge', param: 'search' };
        }
        // 기본: 채용공고 검색
        return { placeholder: recruitSearchPlaceholder, path: '/recruitments/region', param: 'search' };
    };

    const searchConfig = getSearchConfig();

    const handleSearch = () => {
        if (searchQuery.trim()) {
            navigate(`${searchConfig.path}?${searchConfig.param}=${encodeURIComponent(searchQuery.trim())}`);
        }
    };

    const handleClearSearch = () => {
        setSearchQuery('');
        // 현재 페이지에서 search 파라미터만 제거
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('search');
        const queryString = newParams.toString();
        navigate(queryString ? `${location.pathname}?${queryString}` : location.pathname);
    };

    const handleSearchKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    // 모바일 상세 페이지: 뒤로가기 + 타이틀만 있는 iOS 스타일 네비게이션으로 전환
    // 헤더 CSS 브레이크포인트(768px)와 일치시켜야 함 (useIsMobile은 MUI md=900px 기준이라 사용 불가)
    const isMobileHeader = useMediaQuery('(max-width: 768px)');
    const detailNav = isMobileHeader ? getDetailNavConfig(location.pathname) : null;

    const handleDetailNavBack = () => {
        if (handlePageHeaderBack()) return;
        // 직접 URL 진입(히스토리 없음)이면 목록으로 이동
        if (location.key !== 'default') {
            navigate(-1);
        } else if (detailNav) {
            navigate(detailNav.fallbackPath);
        }
    };

    const handleCopyUrl = async () => {
        // 공유 유입 추적: 복사 링크에 UTM 부착(카톡·인스타 공유는 referrer가 비어 UTM으로만 잡힘).
        // 기존 utm이 있으면 건드리지 않고, 없을 때만 share 태그를 붙인다.
        //
        // utm_campaign은 어드민 캠페인(campaigns.campaign_key)과 조인되는 유일한 키라 반드시 넣는다 —
        // 빠지면 공유 유입이 캠페인 성과에 영영 안 잡힌다. 카페 자동게시(cafe_recruit)·브리핑 메일(brief_email)과
        // 동일 규약: **campaignKey는 고정**하고, 무엇을 공유했는지는 utm_content(경로)로 구분한다.
        const buildShareUrl = (): string => {
            try {
                const u = new URL(window.location.href);
                if (!u.searchParams.has('utm_source')) {
                    u.searchParams.set('utm_source', 'share');
                    u.searchParams.set('utm_medium', 'link');
                    u.searchParams.set('utm_campaign', SHARE_CAMPAIGN_KEY);
                    u.searchParams.set('utm_content', toShareContentTag(u.pathname));
                }
                return u.toString();
            } catch {
                return window.location.href;
            }
        };
        const url = await shortShareUrl(buildShareUrl());
        // 공유 클릭 기록 — 속닥속닥·전문가 라운지 글 상세만(다른 화면 공유는 지금 지표에 쓰지 않는다)
        const shareTarget = detailTargetOfPath(location.pathname);
        if (shareTarget) track.click({ screen: shareTarget.screen }, { kind: shareTarget.kind, id: shareTarget.id }, { action: UI_ACTION.SHARE });
        try {
            await navigator.clipboard.writeText(url);
            setToast({ show: true, message: '링크가 복사되었습니다.', type: 'success' });
        } catch {
            // clipboard API 미지원/권한 거부 시 폴백
            const textarea = document.createElement('textarea');
            textarea.value = url;
            document.body.appendChild(textarea);
            textarea.select();
            document.execCommand('copy');
            document.body.removeChild(textarea);
            setToast({ show: true, message: '링크가 복사되었습니다.', type: 'success' });
        }
    };

    const handleRecruitmentNavigation = () => {
        // 채용 기본 진입은 전국(0_0). 백엔드가 로그인 시 내 지역 우선 정렬을 처리.
        navigateOrScrollToTop('/recruitments/region?category=REGION&tab=region&districts=0_0');
    };

    return (
        <>
            <HeaderWrapper>
                {detailNav ? (
                    <DetailNavBar>
                        <DetailNavBackButton onClick={handleDetailNavBack} aria-label="뒤로가기">
                            <ArrowBackIosNewIcon sx={{ fontSize: 20 }} />
                        </DetailNavBackButton>
                        <LogoWrapper onClick={() => navigateOrScrollToTop('/')}>
                            <BrandLogo>명치사</BrandLogo>
                        </LogoWrapper>
                        <DetailNavActions>
                            <DetailNavIconButton onClick={handleCopyUrl} aria-label="링크 복사">
                                <IosShareIcon sx={{ fontSize: 22 }} />
                            </DetailNavIconButton>
                            <DetailNavIconButton onClick={() => navigate('/search')} aria-label="검색">
                                <HeaderSearchIcon width={24} height={24} />
                            </DetailNavIconButton>
                            {isLoggedIn && (
                                <BellButton onClick={goNotifications} aria-label="알림">
                                    <BellIcon sx={{ fontSize: 24 }} />
                                    {unreadCount > 0 && <BellBadge>{bellLabel}</BellBadge>}
                                </BellButton>
                            )}
                            <DetailNavIconButton onClick={() => setMobileMenuOpen(true)} aria-label="메뉴">
                                <MenuIcon sx={{ fontSize: 24 }} />
                            </DetailNavIconButton>
                        </DetailNavActions>
                    </DetailNavBar>
                ) : (
                    <>
                <TopAnnouncementBar />

                <MainHeader>
                    <HeaderContainer>
                        <TopSection>
                            <LogoWrapper onClick={() => navigateOrScrollToTop('/')}>
                                <BrandLogo>명치사</BrandLogo>
                            </LogoWrapper>
                            
                            <SearchWrapper>
                                <SearchInput
                                    type="text"
                                    placeholder={searchConfig.placeholder}
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    onKeyPress={handleSearchKeyPress}
                                />
                                {searchQuery && (
                                    <ClearButton onClick={handleClearSearch}>
                                        <CloseIcon sx={{ fontSize: '14px' }} />
                                    </ClearButton>
                                )}
                                <SearchIconWrapper onClick={handleSearch}>
                                    <SearchIcon sx={{ fontSize: '20px' }} />
                                </SearchIconWrapper>
                            </SearchWrapper>
                            <MobileSearchButton onClick={() => navigate('/search')}>
                                <HeaderSearchIcon width={24} height={24} />
                            </MobileSearchButton>
                            {/* 모바일 헤더(≤768px)에서만 — 769~899px 는 PC 메뉴 줄에 종이 있어 둘이 겹쳐 보였다 */}
                            {isLoggedIn && isMobileHeader && (
                                <BellButton onClick={goNotifications} aria-label="알림">
                                    <BellIcon sx={{ fontSize: 24 }} />
                                    {unreadCount > 0 && <BellBadge>{bellLabel}</BellBadge>}
                                </BellButton>
                            )}
                            <MobileHamburgerButton onClick={() => setMobileMenuOpen(true)} aria-label="메뉴">
                                <MenuIcon sx={{ fontSize: 24 }} />
                            </MobileHamburgerButton>
                        </TopSection>

                        <Nav>
                            <MenuList>
                                <MenuItem onClick={handleRecruitmentNavigation}>
                                    채용정보
                                </MenuItem>
                                <MenuItem onClick={() => { navigate('/seminars'); window.scrollTo(0, 0); }}>
                                    세미나
                                </MenuItem>
                                <MenuItem onClick={() => { navigate('/posts'); window.scrollTo(0, 0); }}>
                                    속닥속닥
                                </MenuItem>
                                <MenuItem onClick={() => { navigate('/expert-lounge'); window.scrollTo(0, 0); }}>
                                    전문가 라운지
                                </MenuItem>
                                <MenuItem onClick={() => { navigate('/notices'); window.scrollTo(0, 0); }}>
                                    공지사항
                                </MenuItem>
                            </MenuList>
                            
                            <UserMenuList>
                                {isLoggedIn ? (
                                    <>
                                        <UserBellItem onClick={goNotifications} aria-label="알림">
                                            <BellIcon sx={{ fontSize: 22 }} />
                                            {unreadCount > 0 && <BellBadge>{bellLabel}</BellBadge>}
                                        </UserBellItem>
                                        {/* 치과 회원은 이름 대신 [채용 등록](2026-10-06) — 일반·전문가 회원은 이름 그대로 */}
                                        {user?.role?.includes('ROLE_CLINIC') ? (
                                            <UserPrimaryItem onClick={() => { navigate('/recruitments/new'); window.scrollTo(0, 0); }}>
                                                채용 등록
                                            </UserPrimaryItem>
                                        ) : (
                                            <UserNameItem>
                                                {user?.name || '사용자'}님
                                            </UserNameItem>
                                        )}
                                        <UserMenuItem onClick={() => { navigate('/profile'); window.scrollTo(0, 0); }}>
                                            마이페이지
                                        </UserMenuItem>
                                        <UserMenuItem onClick={() => logout()}>
                                            로그아웃
                                        </UserMenuItem>
                                    </>
                                ) : (
                                    <>
                                        <UserMenuItem onClick={() => { navigate('/login'); window.scrollTo(0, 0); }}>
                                            로그인
                                        </UserMenuItem>
                                        <UserMenuItem onClick={() => { navigate('/signup'); window.scrollTo(0, 0); }}>
                                            회원가입
                                        </UserMenuItem>
                                        <UserMenuItem onClick={() => handleLoginRequiredAction('/resume')}>
                                            이력서 등록
                                        </UserMenuItem>
                                        {/* [공고등록]은 치과 회원에게만(2026-09-30) — 비로그인 헤더에서는 뺀다 */}
                                    </>
                                )}
                            </UserMenuList>
                        </Nav>
                    </HeaderContainer>
                </MainHeader>
                    </>
                )}
            </HeaderWrapper>

            {/* Mobile Slide Menu — 우측 서랍 메뉴(회원/치과/비로그인 3변형) */}
            <MobileDrawerMenu
                open={mobileMenuOpen}
                onClose={closeMobileMenu}
                user={user}
                isLoggedIn={isLoggedIn}
                onNavigate={handleMobileMenuNavigate}
                onLogout={handleMobileMenuLogout}
            />

            <BottomNav>
                <BottomNavItem
                    active={location.pathname === '/' || location.pathname === '/recruitments'}
                    onClick={() => navigateOrScrollToTop('/')}
                >
                    <BottomHomeIcon width={24} height={24} />
                    <BottomNavLabel>홈</BottomNavLabel>
                </BottomNavItem>
                <BottomNavItem
                    active={location.pathname.startsWith('/recruitments/region')}
                    onClick={handleRecruitmentNavigation}
                >
                    <BottomHiringIcon width={24} height={24} />
                    <BottomNavLabel>채용</BottomNavLabel>
                </BottomNavItem>
                <BottomNavItem
                    active={location.pathname.startsWith('/seminars')}
                    onClick={() => navigateOrScrollToTop('/seminars')}
                >
                    <BottomSeminarIcon width={24} height={24} />
                    <BottomNavLabel>세미나</BottomNavLabel>
                </BottomNavItem>
                <BottomNavItem
                    active={location.pathname.startsWith('/posts')}
                    onClick={() => navigateOrScrollToTop('/posts')}
                >
                    <BottomCommunityIcon width={24} height={24} />
                    <BottomNavLabel>속닥속닥</BottomNavLabel>
                </BottomNavItem>
                <BottomNavItem
                    active={location.pathname.startsWith('/expert-lounge')}
                    onClick={() => navigateOrScrollToTop('/expert-lounge')}
                >
                    <LightbulbOutlinedIcon sx={{ fontSize: 24 }} />
                    <BottomNavLabel>전문가</BottomNavLabel>
                </BottomNavItem>
                <BottomNavItem
                    active={location.pathname.startsWith('/profile')}
                    onClick={() => handleLoginRequiredAction('/profile')}
                >
                    <BottomMyPageIcon width={24} height={24} />
                    <BottomNavLabel>MY</BottomNavLabel>
                    {unreadCount > 0 && <NavDot />}
                </BottomNavItem>
            </BottomNav>

            <CustomModal
                title='알림'
                width={isMobile ? '90%' : '400px'}
                onClose={closeAlert}
                open={showAlertModal}
                height='auto'
            >
                <Container width={"100%"} display='block'>
                    <Text textalign='center' size='16px' style={{ marginBottom: '20px' }}>
                        {alertMessage}
                    </Text>
                    <ModalButton
                        onClick={closeAlert}
                        justifycontents='center'
                        color='#fff'
                        bgcolor={designSystem.colors.primary}
                    >
                        확인
                    </ModalButton>
                </Container>
            </CustomModal>

            <CustomModal
                title='로그인'
                width={isMobile ? '90%' : '400px'}
                onClose={handleLoginRequiredModalClose}
                open={showLoginRequiredModal}
                height='auto'
            >
                <Container width={"100%"} display='block'>
                    <Text textalign='center' size='16px' style={{ marginTop: '12px', marginBottom: '20px' }}>
                        <strong>로그인이 필요한 서비스 입니다.</strong><br />
                        로그인 하시겠습니까?
                    </Text>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <ModalButton
                            onClick={handleLoginRequiredModalClose}
                            justifycontents='center'
                            color='#666'
                            bgcolor='#f3f4f6'
                            style={{ flex: 1 }}
                        >
                            취소
                        </ModalButton>
                        <ModalButton
                            onClick={handleLoginConfirm}
                            justifycontents='center'
                            color='#fff'
                            bgcolor={designSystem.colors.primary}
                            style={{ flex: 1 }}
                        >
                            로그인
                        </ModalButton>
                    </div>
                </Container>
            </CustomModal>
        </>
    );
};

export default Header;
